import React from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { NodeConfig } from "../../types/algoStudio";

const CanvasWrapper = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 100%;
  background-color: #090a0f;
  border: 1px dashed #252a38;
  border-radius: 8px;
  overflow: hidden;
`;

const DropIndicator = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(107, 159, 255, 0.08);
  border: 1px dashed #6b9fff;
  pointer-events: none;
`;

interface CanvasProps {
  children: React.ReactNode;
  onDrop: (type: string, x: number, y: number) => void;
}

const Canvas: React.FC<CanvasProps> = ({ children, onDrop }) => {
  const [isOver, setIsOver] = React.useState(false);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setIsOver(true);
  };

  const handleDragLeave = () => {
    setIsOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsOver(false);

    try {
      const data = JSON.parse(e.dataTransfer.getData("application/json"));
      const canvasElement = e.currentTarget;
      const rect = canvasElement.getBoundingClientRect();

      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      onDrop(data.type, x, y);
    } catch (error) {
      console.error("Error handling drop:", error);
    }
  };

  return (
    <CanvasWrapper
      className="canvas"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      animate={{
        backgroundColor: isOver ? "#181b24" : "#090a0f",
      }}
      transition={{
        duration: 0.2,
      }}
    >
      <AnimatePresence>
        {isOver && (
          <DropIndicator
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              duration: 0.2,
            }}
          />
        )}
      </AnimatePresence>
      {children}
    </CanvasWrapper>
  );
};

export default Canvas;
